import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  ContactSupport,
  People,
  Campaign,
  Assessment,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/useStore";
import { useNotification } from "../../components/common/NotificationProvider";
import {
  useFormValidation,
  commonValidationRules,
} from "../../hooks/useFormValidation";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`crm-tabpanel-${index}`}
      aria-labelledby={`crm-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CRM: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const { patients, addPatient, updatePatient, deletePatient } = useStore();
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [editingPatient, setEditingPatient] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    lastVisit: "",
    totalVisits: "",
    status: "Active",
    loyaltyPoints: "",
    mrn: "",
    age: "",
    gender: "",
    department: "",
    room: "",
    doctor: "",
    admissionDate: "",
  });

  const validationRules = {
    name: commonValidationRules.name,
    email: { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
    phone: { required: true, minLength: 10 },
    lastVisit: commonValidationRules.date,
    totalVisits: { required: true, min: 0 },
    loyaltyPoints: { required: true, min: 0 },
    mrn: { required: true, minLength: 5 },
    age: { required: true, min: 0, max: 150 },
    gender: { required: true },
    department: { required: true },
    room: { required: true },
    doctor: { required: true },
    admissionDate: commonValidationRules.date,
  };

  const { errors, validateForm, validateFieldOnChange, clearErrors } =
    useFormValidation(validationRules);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (type: string, patient?: any) => {
    setDialogType(type);
    if (patient) {
      setEditingPatient(patient);
      setFormData({
        name: patient.name,
        email: patient.email || "",
        phone: patient.phone || "",
        lastVisit: patient.lastVisit || "",
        totalVisits: patient.totalVisits?.toString() || "",
        status: patient.status,
        loyaltyPoints: patient.loyaltyPoints?.toString() || "",
        mrn: patient.mrn,
        age: patient.age.toString(),
        gender: patient.gender,
        department: patient.department,
        room: patient.room,
        doctor: patient.doctor,
        admissionDate: patient.admissionDate,
      });
    } else {
      setEditingPatient(null);
      setFormData({
        name: "",
        email: "",
        phone: "",
        lastVisit: "",
        totalVisits: "",
        status: "Active",
        loyaltyPoints: "",
        mrn: "",
        age: "",
        gender: "",
        department: "",
        room: "",
        doctor: "",
        admissionDate: "",
      });
    }
    clearErrors();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogType("");
    setEditingPatient(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      lastVisit: "",
      totalVisits: "",
      status: "Active",
      loyaltyPoints: "",
      mrn: "",
      age: "",
      gender: "",
      department: "",
      room: "",
      doctor: "",
      admissionDate: "",
    });
    clearErrors();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    validateFieldOnChange(field, value);
  };

  const handleSubmit = () => {
    if (!validateForm(formData)) {
      showError("Please fix the form errors before submitting");
      return;
    }

    try {
      const patientData = {
        name: formData.name,
        mrn: formData.mrn,
        age: parseInt(formData.age),
        gender: formData.gender,
        admissionDate: formData.admissionDate,
        department: formData.department,
        status: formData.status,
        room: formData.room,
        doctor: formData.doctor,
        email: formData.email,
        phone: formData.phone,
        lastVisit: formData.lastVisit,
        totalVisits: parseInt(formData.totalVisits),
        loyaltyPoints: parseInt(formData.loyaltyPoints),
      };

      if (editingPatient) {
        updatePatient(editingPatient.id, patientData);
        showSuccess("Patient updated successfully");
      } else {
        addPatient(patientData);
        showSuccess("Patient added successfully");
      }
      handleCloseDialog();
    } catch (error) {
      showError("Failed to save patient");
    }
  };

  const handleDelete = (id: number) => {
    try {
      deletePatient(id);
      showSuccess("Patient deleted successfully");
    } catch (error) {
      showError("Failed to delete patient");
    }
  };

  // Use store data directly in the table

  const campaignsData = [
    {
      id: 1,
      name: "Health Checkup Campaign",
      type: "Email",
      targetAudience: "All Patients",
      startDate: "2024-01-01",
      endDate: "2024-01-31",
      status: "Active",
      sent: 1250,
      opened: 850,
      clicked: 320,
    },
    {
      id: 2,
      name: "Vaccination Reminder",
      type: "SMS",
      targetAudience: "Pediatric Patients",
      startDate: "2024-01-15",
      endDate: "2024-02-15",
      status: "Active",
      sent: 450,
      opened: 420,
      clicked: 180,
    },
    {
      id: 3,
      name: "Annual Health Screening",
      type: "Email",
      targetAudience: "Adults 40+",
      startDate: "2023-12-01",
      endDate: "2023-12-31",
      status: "Completed",
      sent: 800,
      opened: 600,
      clicked: 250,
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "success";
      case "Inactive":
        return "error";
      case "Completed":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("crm.title")}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Customer relationship management and patient engagement
      </Typography>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3, width: "100%" }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <People color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">1,234</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Patients
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Campaign color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">15</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Campaigns
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Assessment color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">4.5/5</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Patient Satisfaction
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ContactSupport color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">92%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Response Rate
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Patient Management" />
            <Tab label="Marketing Campaigns" />
            <Tab label="Surveys & Feedback" />
            <Tab label="Reports" />
          </Tabs>
        </Box>

        {/* Patient Management Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Patient Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("patient")}
            >
              Add Patient
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Last Visit</TableCell>
                  <TableCell>Total Visits</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Loyalty Points</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patients.map((patient) => (
                  <TableRow key={patient.id}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                          {patient.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {patient.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {patient.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{patient.phone}</TableCell>
                    <TableCell>{patient.lastVisit}</TableCell>
                    <TableCell>{patient.totalVisits}</TableCell>
                    <TableCell>
                      <Chip
                        label={patient.status}
                        color={getStatusColor(patient.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{patient.loyaltyPoints}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog("patient", patient)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(patient.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Marketing Campaigns Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Marketing Campaigns</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("campaign")}
            >
              Create Campaign
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Campaign Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Target Audience</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Sent</TableCell>
                  <TableCell align="right">Opened</TableCell>
                  <TableCell align="right">Clicked</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaignsData.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell>{campaign.name}</TableCell>
                    <TableCell>{campaign.type}</TableCell>
                    <TableCell>{campaign.targetAudience}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {campaign.startDate}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          to {campaign.endDate}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={campaign.status}
                        color={getStatusColor(campaign.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{campaign.sent}</TableCell>
                    <TableCell align="right">{campaign.opened}</TableCell>
                    <TableCell align="right">{campaign.clicked}</TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Surveys & Feedback Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Surveys & Feedback
          </Typography>
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Patient Satisfaction Survey
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Overall satisfaction with services
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    View Results
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Service Quality Feedback
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Quality of medical services provided
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    View Results
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Staff Performance Review
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Patient feedback on staff performance
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    View Results
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Reports Tab */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            CRM Reports
          </Typography>
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Patient Analytics
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Patient demographics and behavior analysis
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Campaign Performance
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Marketing campaign effectiveness metrics
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Customer Lifetime Value
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Patient value and retention analysis
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Dialog for adding new items */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "patient" &&
            (editingPatient ? "Edit Patient" : "Add New Patient")}
          {dialogType === "campaign" && "Create Marketing Campaign"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogType === "patient" ? (
              <>
                <TextField
                  fullWidth
                  label="Name"
                  variant="outlined"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="MRN"
                  variant="outlined"
                  value={formData.mrn}
                  onChange={(e) => handleInputChange("mrn", e.target.value)}
                  error={!!errors.mrn}
                  helperText={errors.mrn}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Age"
                  type="number"
                  variant="outlined"
                  value={formData.age}
                  onChange={(e) => handleInputChange("age", e.target.value)}
                  error={!!errors.age}
                  helperText={errors.age}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    label="Gender"
                    value={formData.gender}
                    onChange={(e) =>
                      handleInputChange("gender", e.target.value)
                    }
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Admission Date"
                  type="date"
                  variant="outlined"
                  value={formData.admissionDate}
                  onChange={(e) =>
                    handleInputChange("admissionDate", e.target.value)
                  }
                  error={!!errors.admissionDate}
                  helperText={errors.admissionDate}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Department</InputLabel>
                  <Select
                    label="Department"
                    value={formData.department}
                    onChange={(e) =>
                      handleInputChange("department", e.target.value)
                    }
                  >
                    <MenuItem value="Cardiology">Cardiology</MenuItem>
                    <MenuItem value="Obstetrics">Obstetrics</MenuItem>
                    <MenuItem value="Emergency">Emergency</MenuItem>
                    <MenuItem value="Surgery">Surgery</MenuItem>
                    <MenuItem value="Pediatrics">Pediatrics</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Room"
                  variant="outlined"
                  value={formData.room}
                  onChange={(e) => handleInputChange("room", e.target.value)}
                  error={!!errors.room}
                  helperText={errors.room}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Doctor"
                  variant="outlined"
                  value={formData.doctor}
                  onChange={(e) => handleInputChange("doctor", e.target.value)}
                  error={!!errors.doctor}
                  helperText={errors.doctor}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  variant="outlined"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  variant="outlined"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Last Visit"
                  type="date"
                  variant="outlined"
                  value={formData.lastVisit}
                  onChange={(e) =>
                    handleInputChange("lastVisit", e.target.value)
                  }
                  error={!!errors.lastVisit}
                  helperText={errors.lastVisit}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Total Visits"
                  type="number"
                  variant="outlined"
                  value={formData.totalVisits}
                  onChange={(e) =>
                    handleInputChange("totalVisits", e.target.value)
                  }
                  error={!!errors.totalVisits}
                  helperText={errors.totalVisits}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={formData.status}
                    onChange={(e) =>
                      handleInputChange("status", e.target.value)
                    }
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Loyalty Points"
                  type="number"
                  variant="outlined"
                  value={formData.loyaltyPoints}
                  onChange={(e) =>
                    handleInputChange("loyaltyPoints", e.target.value)
                  }
                  error={!!errors.loyaltyPoints}
                  helperText={errors.loyaltyPoints}
                  sx={{ mb: 2 }}
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Name"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={
              dialogType === "patient" ? handleSubmit : handleCloseDialog
            }
          >
            {dialogType === "patient"
              ? editingPatient
                ? "Update"
                : "Add"
              : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CRM;
